import { createContext, useState, useContext, ReactNode, useEffect } from 'react'
import { useLocation } from 'react-router-dom'


type IContextOptions = {
    isPdf: boolean,
    fromDate?:string,
    toDate?:string,
    reportName?: string
    campaigns?: string[]
    publishers?: string[]
    channels?: string[]
    networks?: string[]
    countries?: string[]
    alertIds?: string[]
    includeAlerts?: boolean
    includeMonthlyPerformance?: boolean
    includeKeyMetrics?: boolean
    reportTimeZone?: string
}
type AppContextType = {
  isPdf: boolean
  fromDate?:string
  toDate?:string
  reportName?: string
  campaigns?: string[]
  publishers?: string[]
  channels?: string[]
  networks?: string[]
  countries?: string[]
  alertIds?: string[]
  includeAlerts?: boolean
  includeMonthlyPerformance?: boolean
  includeKeyMetrics?: boolean
  reportTimeZone?: string
  setPdfInfo: (opts: IContextOptions) => void
}

const defaultState: AppContextType = {
    isPdf: false,
    setPdfInfo: () => {},
}

const AppContext = createContext<AppContextType>(defaultState)

function readContextOptions(search: string): IContextOptions {
    const queryParams = new URLSearchParams(search)
    const pdfParam = queryParams.get('isPdf')
    if(pdfParam === null) return { isPdf: false }

    const splitParam = (name: string) => (queryParams.get(name) || '').split(',').filter(Boolean)
    return {
        isPdf: pdfParam === 'true',
        fromDate: queryParams.get('from') || undefined,
        toDate: queryParams.get('to') || undefined,
        campaigns: splitParam('campaigns'),
        publishers: splitParam('publishers'),
        channels: splitParam('channels'),
        networks: splitParam('networks'),
        countries: splitParam('countries'),
        alertIds: splitParam('alertIds'),
        reportName: queryParams.get('reportName') || undefined,
        includeAlerts: queryParams.get('includeAlerts') === 'true',
        includeMonthlyPerformance: queryParams.get('includeMonthlyPerformance') === 'true',
        includeKeyMetrics: queryParams.get('includeKeyMetrics') === 'true',
        reportTimeZone: queryParams.get('reportTimeZone') || 'UTC',
    }
}

export const AppProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  const [pdfInfo, setPdfInfo] = useState<IContextOptions>(() => readContextOptions(location.search))

  useEffect(() => {
    setPdfInfo(readContextOptions(location.search))
  }, [location.search])

  return (
    <AppContext.Provider value={{ ...pdfInfo, setPdfInfo }}>
      {children}
    </AppContext.Provider>
  )
}

export const useAppContext = () => useContext(AppContext)
